import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Sparkles } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getOrderedTypes, type CelebrationTypeEntry } from '@/config/celebrationRegistry';
import { isSingleEventType } from '@/lib/celebrations/celebrationTemplateMatching';
import { useCelebrationTemplates } from '@/hooks/useCelebrationTemplates';

const TIER_META: Record<number, { title: string; badge: string }> = {
  1: { title: '⚡ Popular Celebrations', badge: 'Ready in ~30 sec' },
  2: { title: '🌟 Cultural & Life Events', badge: 'Traditions & milestones' },
  3: { title: '📅 Community & More', badge: 'Reunions, festivals & more' },
};

export const CelebrationCreateEntry: React.FC = () => {
  const navigate = useNavigate();
  const { templates, loading } = useCelebrationTemplates();

  const handleSelectType = (typeKey: string) => {
    navigate(`/quick?type=${encodeURIComponent(typeKey)}`);
  };

  const orderedTypes = getOrderedTypes();
  const quickTypes = loading ? [] : orderedTypes.filter((type) => isSingleEventType(templates, type.key));
  const tiers = orderedTypes.reduce<Record<number, CelebrationTypeEntry[]>>((acc, type) => {
    (acc[type.tier] ??= []).push(type);
    return acc;
  }, {});


  return (
    <PageContainer width="default" className="space-y-6">
      <AppBreadcrumbs />

      <section className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Create a Celebration</h1>
        <p className="text-muted-foreground">
          Choose a celebration type or use AI Chat to build your event instantly.
        </p>
      </section>

      {/* Hero AI Chat CTA Banner */}
      <Card className="border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Bot className="h-6 w-6 text-primary animate-pulse" />
              Build with AI Chat
            </CardTitle>

            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3 text-amber-500" />
              Fastest
            </Badge>

          </div>
          <CardDescription className="text-base">
            Describe your event in plain text and let AI set up your schedule, tags, and tools automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="lg" className="w-full sm:w-auto text-base gap-2" onClick={() => navigate('/celebrations/new?mode=chat')}>
            <Bot className="h-5 w-5" />
            💬 Start with Chat
          </Button>
        </CardContent>
      </Card>

      {quickTypes.length > 0 && (
        <section className="space-y-3 pt-2">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight">⚡ Quick Single-Event Celebrations</h2>
            <Badge variant="outline" className="text-xs">Ready in ~30 sec</Badge>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {quickTypes.map((type) => (
              <Card
                key={`quick-${type.key}`}
                className="cursor-pointer border-primary/20 hover:border-primary/50 hover:shadow-md transition-all group"
                onClick={() => handleSelectType(type.key)}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{type.emoji}</span>
                    <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {type.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{type.tagline}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {[1, 2, 3].map((tier) => {
        const types = tiers[tier];
        if (!types || types.length === 0) return null;
        const meta = TIER_META[tier];
        return (
          <section key={tier} className="space-y-3 pt-2">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight">{meta.title}</h2>
              <Badge variant="outline" className="text-xs">{meta.badge}</Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {types.map((type) => (
                <Card
                  key={type.key}
                  className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                  onClick={() => handleSelectType(type.key)}
                >
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{type.emoji}</span>
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {type.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{type.tagline}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        );
      })}
    </PageContainer>
  );
};
